import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Gantt, TaskModel } from '@bryntum/gantt';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { useGanttSettingsStore } from 'store/ganttSettings.store';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ganttConfigSharedResourses, projectConfig } from 'components/common/bryntumGantt/GanttConfig';
import { CustomGantt } from 'components/common/bryntumGantt/gantt';
import { taskTooltipFeatureSharedResourses } from 'components/common/bryntumGantt/ganttFeatures';
import { getSharedResourcesAvailability } from 'components/common/bryntumGantt/helper';

import { riskConfig } from 'components/Dashboards/ExcellenceApps/riskConfig';
import { Select } from 'components/DesignSystem/Select';
import { CustomizedTooltip } from 'components/common';
import {
    ISharedResourcesAvailabilityCluster,
    ISharedResourcesAvailabilityResponse,
} from 'components/widgets/Gantt/components/ganttChart.types';
import classes from 'components/widgets/SharedResourcesAvailability/ganttChartDisplay/ganttChartDisplay.module.scss';

const groupViewList: ISelectOption<string>[] = [
    {
        value: 'expandAll',
        label: 'Expand All',
    },
    {
        value: 'collapseAll',
        label: 'Collapse All',
    },
];
interface IGanttChartDisplayProps {
    ganttData?: ISharedResourcesAvailabilityResponse;
    isLoading: boolean;
    isFullScreen: boolean;
    getInstanceAgain: (gantt: any) => void;
    isCriticalPath: boolean;
    selectedCluster: ISharedResourcesAvailabilityCluster | undefined;
    className?: string;
}

const GanttChartDisplay = ({
    isFullScreen,
    ganttData,
    isLoading,
    getInstanceAgain,
    isCriticalPath,
    selectedCluster,
    className,
}: IGanttChartDisplayProps) => {
    const ganttInstance = useRef<Gantt | null>(null);
    const { calendarViewRange, showActivityLabels, showProgressLine, showBaseline } = useGanttSettingsStore();
    const [view, setView] = useState<ISelectOption<string> | null>(groupViewList[0]);
    const resources = useMemo(() => selectedCluster?.resourses, [selectedCluster]);

    const getInstance = useCallback((gantt) => {
        if (gantt) {
            ganttInstance.current = gantt;
            getInstanceAgain(gantt);
        }
    }, []);

    const { versionDate, flatTasks, dependencies } = useMemo(
        () => ({
            versionDate: ganttData?.versionDate,
            flatTasks: getSharedResourcesAvailability({ ganttData, clusterId: selectedCluster?.clusterId }),
            dependencies: [],
        }),
        [ganttData, selectedCluster],
    );

    useEffect(() => {
        //expand all when selected cluster changed
        const expandAllView = groupViewList[0];

        if (selectedCluster) {
            setView(expandAllView);
        }
    }, [selectedCluster]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.features.labels.disabled = !showActivityLabels;
        }
    }, [showActivityLabels]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.features.progressLine.disabled = !showProgressLine;
        }
    }, [showProgressLine]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.features.baselines.disabled = !showBaseline;
        }
    }, [showBaseline]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.viewPreset = calendarViewRange;
        }
    }, [calendarViewRange]);

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance && instance.project) {
            const store = instance.project.taskStore;
            if (isCriticalPath) {
                store.filter({
                    id: 'criticalPathFilter',
                    filterBy: (record) => record.originalData.criticalPath,
                });
            } else {
                store.removeFilter('criticalPathFilter');
            }
        }
    }, [isCriticalPath]);

    const zoomIn = () => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.zoomIn();
        }
    };

    const zoomOut = () => {
        const instance = ganttInstance.current;
        if (instance) {
            instance.zoomOut();
        }
    };

    useEffect(() => {
        const instance = ganttInstance.current;
        if (instance && instance.project) {
            const store = instance.project.taskStore;
            const firstTask = store.first;
            instance.scrollTaskIntoView(firstTask as TaskModel);
        }
    }, [flatTasks]);

    const handleExpandCollapseAll = (option: ISelectOption<string> | null) => {
        const action = option?.value;
        const instance = ganttInstance.current;

        if (instance && action) {
            switch (action) {
                case 'expandAll':
                    instance.expandAll();
                    break;
                case 'collapseAll':
                    instance.collapseAll();
                    break;
            }
        }
        setView(option);
    };

    const clusterDetailsLink = AppRoutes.buildPathWithQuery(
        [AppRoutes.DASHBOARD_RISK_APPS_PATH, riskConfig.durationForecast.link],
        { clusterId: selectedCluster?.clusterId },
    );

    return (
        <div className={classes.ganttChartDisplayContainer}>
            <div className={classes.progressSelection}>
                <div className={classes.ganttOptionsAndInfoWrapper}>
                    <div className={classes.resourses}>
                        Cluster Resources:{' '}
                        {resources && resources?.length > 0 ? resources.toString() : 'No resources defined'}
                    </div>
                    <div className={classes.filterView}>
                        <Select<ISelectOption<string>>
                            value={view}
                            options={groupViewList}
                            onChange={(value) => handleExpandCollapseAll(value)}
                        />
                    </div>

                    <div className={classes.clusterDetails}>
                        <CustomizedTooltip
                            triggerElement={<Link to={clusterDetailsLink}>Cluster Details</Link>}
                            tooltipContent={'Click to see cluster details'}
                        />
                    </div>
                </div>

                <div className={classes.rowContainer}>
                    <MuiIcon icon={'zoom_in'} fontSize={'2.4rem'} onClick={zoomIn} color={'#7489aa'} />
                    <MuiIcon icon={'zoom_out'} fontSize={'2.4rem'} onClick={zoomOut} color={'#7489aa'} />
                </div>
            </div>

            <div
                className={`${classes.container} ${isFullScreen ? classes.fullScreen : ''} ${className ? className : ''} `}
            >
                {isLoading && (
                    <div className={classes.spinnerContainer}>
                        <OverlayWithSpinner />
                    </div>
                )}

                {flatTasks && flatTasks?.length > 0 && versionDate && (
                    <CustomGantt
                        versionDate={versionDate}
                        getInstance={getInstance}
                        flatTasks={flatTasks}
                        dependencies={dependencies}
                        ganttConfig={ganttConfigSharedResourses}
                        projectConfig={projectConfig}
                        taskTooltipFeature={taskTooltipFeatureSharedResourses}
                        projectLinesFeature={{
                            disabled: true,
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo(GanttChartDisplay);
